/**
 * Custom Form System — client-side handler
 * Handles AJAX submission to Cloudflare Worker, validation,
 * loading/success states, redirect, and GA4 tracking.
 * Progressive enhancement: forms work without JS via standard POST.
 * Version: 1.0.0
 */

class CustomFormSystem {
  constructor(section) {
    this.section = section;
    this.form = section.querySelector('.custom-form__form');
    if (!this.form) return;

    this.endpoint = this.form.dataset.endpoint;
    this.redirectUrl = this.form.dataset.redirect || '';
    this.successMessage = this.form.dataset.successMessage || 'Thank you! Your submission has been received.';
    this.formType = this.form.dataset.formType || 'custom';

    this.submitBtn = this.form.querySelector('.custom-form__submit-btn');
    this.successEl = section.querySelector('.custom-form__success');
    this.errorEl = section.querySelector('.custom-form__error');

    this.isSubmitting = false;

    this._bindEvents();
  }

  _bindEvents() {
    this.form.addEventListener('submit', (e) => this._handleSubmit(e));
  }

  async _handleSubmit(e) {
    e.preventDefault();

    if (this.isSubmitting) return;

    // Clear previous errors
    this._clearErrors();

    // Client-side validation
    if (!this._validate()) return;

    this.isSubmitting = true;
    this._setLoadingState(true);

    try {
      const formData = new FormData(this.form);
      const payload = this._buildPayload(formData);

      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        this._handleSuccess(result);
      } else {
        this._handleError(result.errors || [{ message: result.message || 'Submission failed. Please try again.' }]);
      }
    } catch (err) {
      this._handleError([{ message: 'A network error occurred. Please check your connection and try again.' }]);
    } finally {
      this.isSubmitting = false;
      this._setLoadingState(false);
    }
  }

  _buildPayload(formData) {
    const fields = {};
    for (const [key, value] of formData.entries()) {
      // Extract field name from "fields[name]" format
      const match = key.match(/^fields\[(.+)\]$/);
      if (match) {
        fields[match[1]] = value;
      }
    }

    return {
      form_type: this.formType,
      fields: fields,
      meta: {
        page_url: window.location.href,
        referrer: document.referrer || '',
        timestamp: new Date().toISOString()
      }
    };
  }

  _validate() {
    let valid = true;
    const requiredFields = this.form.querySelectorAll('[required]');

    requiredFields.forEach(field => {
      if (!field.value.trim()) {
        this._showFieldError(field, 'This field is required');
        if (valid) field.focus();
        valid = false;
      } else if (field.type === 'email' && !this._isValidEmail(field.value)) {
        this._showFieldError(field, 'Please enter a valid email address');
        if (valid) field.focus();
        valid = false;
      }
    });

    return valid;
  }

  _isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  _showFieldError(field, message) {
    const container = field.closest('.custom-form__field');
    if (!container) return;

    container.classList.add('has-error');
    field.setAttribute('aria-invalid', 'true');

    let errorEl = container.querySelector('.field-error-msg');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'field-error-msg';
      errorEl.setAttribute('role', 'alert');
      container.appendChild(errorEl);
    }
    errorEl.textContent = message;
    errorEl.style.display = 'block';

    const errorId = field.id + '-error';
    errorEl.id = errorId;
    field.setAttribute('aria-describedby', errorId);
  }

  _clearErrors() {
    this.form.querySelectorAll('.has-error').forEach(el => {
      el.classList.remove('has-error');
    });
    this.form.querySelectorAll('[aria-invalid]').forEach(el => {
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
    });
    this.form.querySelectorAll('.field-error-msg').forEach(el => {
      el.style.display = 'none';
    });
    if (this.errorEl) {
      this.errorEl.classList.remove('is-visible');
    }
  }

  _setLoadingState(loading) {
    if (loading) {
      this.section.classList.add('custom-form--submitting');
      if (this.submitBtn) this.submitBtn.disabled = true;
    } else {
      this.section.classList.remove('custom-form--submitting');
      if (this.submitBtn) this.submitBtn.disabled = false;
    }
  }

  _handleSuccess(result) {
    // Track with GA4
    this._trackGA4('form_submission', {
      form_type: this.formType,
      submission_id: result.submission_id || '',
      form_location: window.location.pathname
    });

    // Redirect or show success message
    if (this.redirectUrl) {
      window.location.href = this.redirectUrl;
      return;
    }

    // Show success message inline
    if (this.successEl) {
      this.successEl.textContent = this.successMessage;
      this.successEl.classList.add('is-visible');
      this.successEl.setAttribute('tabindex', '-1');
      this.successEl.focus();
    }

    // Hide the form
    this.form.style.display = 'none';

    // Announce to screen readers
    this._announce(this.successMessage);
  }

  _handleError(errors) {
    const message = errors.map(e => e.message).join('. ');

    if (this.errorEl) {
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
      this.errorEl.setAttribute('tabindex', '-1');
      this.errorEl.focus();
    }

    this._announce(message);
  }

  _announce(message) {
    const el = document.createElement('div');
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.className = 'visually-hidden';
    el.textContent = message;
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 3000);
  }

  _trackGA4(eventName, params) {
    if (typeof gtag === 'function') {
      gtag('event', eventName, params);
    }
  }
}

// Initialize all custom forms on the page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-custom-form]').forEach(section => {
    new CustomFormSystem(section);
  });
});
